import { useMemo, useState } from "react";
import DSCard from "../../../../system-design/DSCard";
import DSCardContent from "../../../../system-design/DSCardContent";
import DSEnumSelect, {
  DSEnumSelectOption,
} from "../../../../system-design/DSEnumSelect";
import DSButton from "../../../../system-design/DSButton";
import DSBox from "../../../../system-design/DSBox";
import DSTypography from "../../../../system-design/DSTypography";
import DSSectionHeading from "../../../../system-design/DSSectionHeading";
import { sectionCardSx } from "../../../../system-design/sectionCardSx";
import {
  DSTable,
  DSTableHead,
  DSTableBody,
  DSTableRow,
  DSTableCell,
  DSTableContainer,
} from "../../../../system-design/Table";
import { MODAL_ACTIONS_INFO } from "../../../../../utils/util";
import {
  DeviceCommandStatus,
  HISTORY_PAGE_SIZE,
  useDeviceCommandsHistory,
  useDevicePendingCommands,
} from "../../../hooks/useDeviceCommands";

const ALL_COMMANDS = "__all__";

// overflowWrap (not wordBreak) only breaks a word when it cannot fit, so a
// narrow card never squeezes a column down to one character per line.
const wrapCellSx = {
  whiteSpace: "normal" as const,
  overflowWrap: "break-word" as const,
};

const cardSx = [
  sectionCardSx,
  { mb: 2, minWidth: 0, maxWidth: "100%" },
] as const;

const STATUS_COLOR: Record<DeviceCommandStatus, string> = {
  PENDING: "warning.main",
  SUCCESS: "success.main",
  FAILURE: "error.main",
};

// Command name (e.g. "playKlydo") -> button headline (e.g. "Preview").
const HEADLINE_BY_NAME = new Map(
  Object.values(MODAL_ACTIONS_INFO).map((info) => [
    info.command,
    info.headline,
  ]),
);

const commandLabel = (name: string) => {
  const headline = HEADLINE_BY_NAME.get(name);
  return headline && headline !== name ? `${headline} (${name})` : name;
};

const formatDate = (date?: Date) =>
  date && date.getTime() > 0 ? date.toLocaleString("en-GB") : "-";

const formatParams = (params: unknown) => {
  if (params === undefined || params === null) return "-";
  if (Array.isArray(params) && params.length === 0) return "-";
  return typeof params === "string" ? params : JSON.stringify(params);
};

const Message = ({
  children,
  error,
}: {
  children: React.ReactNode;
  error?: boolean;
}) => (
  <DSTypography
    variant="body2"
    color={error ? "error" : "text.secondary"}
    sx={{ mt: 1 }}
  >
    {children}
  </DSTypography>
);

export const DevicePendingCommandsCard = ({
  deviceId,
}: {
  deviceId: string;
}) => {
  const { commands, error, isLoading } = useDevicePendingCommands(deviceId);

  return (
    <DSCard sx={cardSx}>
      <DSSectionHeading>
        Commands{commands ? ` (${commands.length})` : ""}
      </DSSectionHeading>
      <DSCardContent>
        {isLoading && <Message>Loading...</Message>}
        {error && (
          <Message error>Failed to load commands: {error.message}</Message>
        )}
        {commands?.length === 0 && (
          <Message>No commands waiting for the clock</Message>
        )}
        {!!commands?.length && (
          <DSTableContainer sx={{ maxHeight: 400, maxWidth: "100%" }}>
            <DSTable size="small" stickyHeader sx={{ width: "100%" }}>
              <DSTableHead>
                <DSTableRow>
                  <DSTableCell>Sent</DSTableCell>
                  <DSTableCell>Command</DSTableCell>
                  <DSTableCell>Params</DSTableCell>
                  <DSTableCell>Expires</DSTableCell>
                </DSTableRow>
              </DSTableHead>
              <DSTableBody>
                {commands.map((command) => (
                  <DSTableRow key={command.id}>
                    <DSTableCell sx={wrapCellSx}>
                      {formatDate(command.time)}
                    </DSTableCell>
                    <DSTableCell sx={wrapCellSx}>
                      {commandLabel(command.name)}
                    </DSTableCell>
                    <DSTableCell sx={wrapCellSx}>
                      {formatParams(command.params)}
                    </DSTableCell>
                    <DSTableCell sx={wrapCellSx}>
                      {formatDate(command.expiresAt)}
                    </DSTableCell>
                  </DSTableRow>
                ))}
              </DSTableBody>
            </DSTable>
          </DSTableContainer>
        )}
      </DSCardContent>
    </DSCard>
  );
};

export const DeviceCommandsHistoryCard = ({
  deviceId,
}: {
  deviceId: string;
}) => {
  const [filter, setFilter] = useState(ALL_COMMANDS);
  const [limit, setLimit] = useState(HISTORY_PAGE_SIZE);
  const { entries, hasMore, error, isLoading } = useDeviceCommandsHistory(
    deviceId,
    limit,
    filter === ALL_COMMANDS ? undefined : filter,
  );

  // Known commands plus any other name seen in the loaded history
  // (for example commands sent by the API crons).
  const filterOptions = useMemo<DSEnumSelectOption<string>[]>(() => {
    const names = new Set<string>(HEADLINE_BY_NAME.keys());
    entries?.forEach((entry) => entry.name && names.add(entry.name));
    if (filter !== ALL_COMMANDS) names.add(filter);
    return [
      { value: ALL_COMMANDS, label: "All commands" },
      ...Array.from(names)
        .map((name) => ({ value: name, label: commandLabel(name) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [entries, filter]);

  return (
    <DSCard sx={cardSx}>
      <DSSectionHeading>Commands History</DSSectionHeading>
      <DSCardContent>
        <DSEnumSelect
          size="small"
          value={filter}
          options={filterOptions}
          onChange={(value) => {
            setFilter(value);
            setLimit(HISTORY_PAGE_SIZE);
          }}
          sx={{ minWidth: 240 }}
        />

        {isLoading && <Message>Loading...</Message>}
        {error && (
          <Message error>
            Failed to load commands history: {error.message}
          </Message>
        )}
        {entries?.length === 0 && <Message>No commands found</Message>}
        {!!entries?.length && (
          <DSTableContainer sx={{ maxHeight: 500, mt: 2, maxWidth: "100%" }}>
            <DSTable size="small" stickyHeader sx={{ width: "100%" }}>
              <DSTableHead>
                <DSTableRow>
                  <DSTableCell>Received</DSTableCell>
                  <DSTableCell>Executed</DSTableCell>
                  <DSTableCell>Command</DSTableCell>
                  <DSTableCell>Params</DSTableCell>
                  <DSTableCell>Status</DSTableCell>
                  <DSTableCell>Reason</DSTableCell>
                </DSTableRow>
              </DSTableHead>
              <DSTableBody>
                {entries.map((entry) => (
                  <DSTableRow key={entry.id}>
                    <DSTableCell sx={wrapCellSx}>
                      {formatDate(entry.receiveTime)}
                    </DSTableCell>
                    <DSTableCell sx={wrapCellSx}>
                      {formatDate(entry.executeTime)}
                    </DSTableCell>
                    <DSTableCell sx={wrapCellSx}>
                      {commandLabel(entry.name)}
                    </DSTableCell>
                    <DSTableCell sx={wrapCellSx}>
                      {formatParams(entry.params)}
                    </DSTableCell>
                    <DSTableCell
                      sx={{
                        color: entry.status
                          ? STATUS_COLOR[entry.status]
                          : undefined,
                        fontWeight: 600,
                      }}
                    >
                      {entry.status ?? "-"}
                    </DSTableCell>
                    <DSTableCell sx={wrapCellSx}>
                      {entry.reason ?? "-"}
                    </DSTableCell>
                  </DSTableRow>
                ))}
              </DSTableBody>
            </DSTable>
          </DSTableContainer>
        )}

        {(hasMore || limit > HISTORY_PAGE_SIZE) && (
          <DSBox sx={{ display: "flex", gap: 1, mt: 1 }}>
            {hasMore && (
              <DSButton
                size="small"
                variant="text"
                onClick={() => setLimit((l) => l + HISTORY_PAGE_SIZE)}
              >
                Show {HISTORY_PAGE_SIZE} more
              </DSButton>
            )}
            {limit > HISTORY_PAGE_SIZE && (
              <DSButton
                size="small"
                variant="text"
                onClick={() => setLimit(HISTORY_PAGE_SIZE)}
              >
                Show less
              </DSButton>
            )}
          </DSBox>
        )}
      </DSCardContent>
    </DSCard>
  );
};
